import {
  DocumentRevisionStatus,
  DocumentRevisionStatusLabels,
  DocumentType,
  ALLOWED_STATUSES_BY_TYPE,
} from '../../types/document-revision-status.types';
import { NotificationStatus } from '../../types/notification.types';

const DEFAULT_BASE_CLASSES = 'px-3 py-1 rounded-full text-xs/3.5 font-medium inline-block w-full';

export function getDocumentRevisionStatusClasses(
  status: DocumentRevisionStatus,
  baseClasses: string = DEFAULT_BASE_CLASSES
): string {
  switch (status) {
    case DocumentRevisionStatus.PENDING_APPROVAL:
      return `${baseClasses} bg-badge-purple-02 text-badge-purple`;
    case DocumentRevisionStatus.APPROVED:
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case DocumentRevisionStatus.FAILED_APPROVAL:
      return `${baseClasses} bg-red-100 text-red-800`;
    case DocumentRevisionStatus.PENDING_VERIFICATION:
      return `${baseClasses} bg-badge-orange-30 text-badge-orange`;
    // Azul, e não roxo: roxo é a Aprovação, e as duas etapas aparecem lado a lado na mesma coluna
    // da Homologação. Com a mesma cor, "Aguardando AP" e "Aguardando VG" não se distinguem.
    case DocumentRevisionStatus.VERIFIED:
      return `${baseClasses} bg-badge-blue-30 text-badge-blue`;
    case DocumentRevisionStatus.FAILED_VERIFICATION:
      return `${baseClasses} bg-red-100 text-red-800`;
    case DocumentRevisionStatus.CURRENT:
      return `${baseClasses} bg-green-20 text-gray-07`;
    case DocumentRevisionStatus.OBSOLETE:
      return `${baseClasses} bg-pink-11 text-red-16`;
    case DocumentRevisionStatus.INACTIVE:
      return `${baseClasses} bg-gray-100 text-gray-600`;
    case DocumentRevisionStatus.PENDING_TEST_CONTENT:
      return `${baseClasses} text-badge-blue bg-badge-blue-30`;
    case DocumentRevisionStatus.VALIDATED:
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case DocumentRevisionStatus.IN_USE:
      return `${baseClasses} bg-green-100 text-green-800`;
    case DocumentRevisionStatus.ARCHIVED:
      return `${baseClasses} bg-gray-100 text-gray-600`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-600`;
  }
}

const STATUS_DOT_BASE = 'inline-block size-5 shrink-0 rounded-full box-border border';

// Exported instead of the base itself: a caller that could read the base could also assemble the
// dot by hand and skip the component that guarantees the shape.
export function getStatusDotClasses(colorClasses: string): string {
  return `${STATUS_DOT_BASE} ${colorClasses}`;
}

export function getDocumentRevisionStatusDotClasses(
  status: DocumentRevisionStatus | string
): string {
  switch (status) {
    case DocumentRevisionStatus.PENDING_APPROVAL:
      return `${STATUS_DOT_BASE} bg-badge-purple-02 border-badge-purple`;
    case DocumentRevisionStatus.APPROVED:
      return `${STATUS_DOT_BASE} bg-blue-100 border-blue-600`;
    case DocumentRevisionStatus.FAILED_APPROVAL:
      return `${STATUS_DOT_BASE} bg-red-100 border-red-16`;
    case DocumentRevisionStatus.PENDING_VERIFICATION:
      return `${STATUS_DOT_BASE} bg-badge-orange-30 border-badge-orange`;
    case DocumentRevisionStatus.VERIFIED:
      return `${STATUS_DOT_BASE} bg-badge-blue-30 border-badge-blue`;
    case DocumentRevisionStatus.FAILED_VERIFICATION:
      return `${STATUS_DOT_BASE} bg-red-100 border-red-16`;
    case DocumentRevisionStatus.CURRENT:
      return `${STATUS_DOT_BASE} bg-green-20 border-green-19`;
    case DocumentRevisionStatus.OBSOLETE:
      return `${STATUS_DOT_BASE} bg-pink-11 border-red-17`;
    case DocumentRevisionStatus.INACTIVE:
      return `${STATUS_DOT_BASE} bg-gray-100 border-gray-400`;
    case DocumentRevisionStatus.PENDING_TEST_CONTENT:
      return `${STATUS_DOT_BASE} bg-badge-blue-30 border-badge-blue`;
    case DocumentRevisionStatus.VALIDATED:
      return `${STATUS_DOT_BASE} bg-blue-100 border-blue-600`;
    case DocumentRevisionStatus.IN_USE:
      return `${STATUS_DOT_BASE} bg-green-20 border-green-19`;
    case DocumentRevisionStatus.ARCHIVED:
      return `${STATUS_DOT_BASE} bg-gray-100 border-gray-400`;
    default:
      return `${STATUS_DOT_BASE} bg-gray-100 border-gray-400`;
  }
}

export function getNotificationStatusClasses(
  status: NotificationStatus,
  baseClasses: string = DEFAULT_BASE_CLASSES
): string {
  switch (status) {
    case NotificationStatus.ACTIVE:
      return `${baseClasses} bg-green-20 text-gray-07`;
    case NotificationStatus.CLOSED:
      return `${baseClasses} bg-gray-02 text-gray-07`;
    case NotificationStatus.DRAFT:
      return `${baseClasses} bg-orange-100 text-orange-400`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-600`;
  }
}

export function getDefaultStatusBaseClasses(): string {
  return DEFAULT_BASE_CLASSES;
}

export function formatDocumentRevisionStatus(status: DocumentRevisionStatus | string): string {
  if (!status) return '';

  if (Object.values(DocumentRevisionStatus).includes(status as DocumentRevisionStatus)) {
    return DocumentRevisionStatusLabels[status as DocumentRevisionStatus];
  }

  // Fallback for any unknown status
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export function isStatusValidForDocumentType(
  status: DocumentRevisionStatus | null | undefined,
  documentType: DocumentType
): boolean {
  if (!status) return false;

  const allowedStatuses = ALLOWED_STATUSES_BY_TYPE[documentType];
  return allowedStatuses.includes(status);
}

export function formatStatusForDocumentType(
  status: DocumentRevisionStatus | null | undefined,
  documentType: DocumentType,
  fallback: string = ''
): string {
  if (!status) return fallback;

  const allowedStatuses = ALLOWED_STATUSES_BY_TYPE[documentType];
  if (allowedStatuses.includes(status)) {
    return DocumentRevisionStatusLabels[status];
  }

  return fallback;
}
